import { useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { ModalContainer } from "../../components/PageComponents";
import {
  CloseIcon,
  CountriesIcon,
  ValidIcon,
  GenerateIcon,
  PercentIcon,
} from "../../icons/promotions";
import { ThreeDotsIcon, CopyIcon, ShareIcon } from "../../icons/promotions";
import { UsageIcon, DiscountIcon, UserGroupIcon } from "../../icons/promotions";
import {
  Input,
  Toggle,
  Select,
  RadioGroup,
  OutlineButton,
  GradientButton,
} from "../../components/forms";
import { PromotionProps, InfoProp } from "./types";

const handleCopyClick = (text: string) => {
  navigator.clipboard.writeText(text);
  toast.success("Copied!");
};

export const Promotion = ({ promotions }: { promotions: PromotionProps[] }) => {
  return (
    // Replaced 'row-8' with flex flex-wrap for adaptive screen stacking
    <div className="flex flex-wrap gap-4 w-full">
      {promotions.map((promotion, index) => {
        return (
          <div
            key={index}
            className="w-full sm:max-w-80 rounded-lg border border-gray-300 p-4 dark:bg-zinc-900 dark:border-zinc-700 flex flex-col gap-4"
          >
            {/* First Line */}
            <div className="flex items-center justify-between">
              <span className="inter-sm-medium-black dark:text-white">
                {promotion.name}
              </span>
              <ThreeDotsIcon />
            </div>

            {/* Promocode */}
            <button
              onClick={() => handleCopyClick(promotion.promoCode)}
              className="w-full flex items-center justify-between border border-gray-400 rounded-lg p-3 dark:border-zinc-600 dark:bg-zinc-800"
            >
              <span className="satoshi-base-bold-gray-900 dark:text-white">
                {promotion.promoCode}
              </span>
              <CopyIcon />
            </button>

            {/* Information */}
            <div className="flex flex-col gap-3">
              <Information
                icon={<CountriesIcon />}
                text="Countries"
                content={promotion.countries}
              />
              <div className="relative">
                <Information
                  icon={<ValidIcon />}
                  text="Valid Till"
                  content={promotion.valid}
                />
                <span
                  className={`absolute right-0 top-1/2 transform -translate-y-1/2 border rounded-2xl py-0.5 px-2 text-xs
                    ${
                      promotion.status === "Ongoing"
                        ? "bg-sux-50 border-sux-200 dark:bg-green-900/20 dark:border-green-800 satoshi-xs-medium-sux-700 dark:text-green-400"
                        : "bg-err-50 border-err-200 dark:bg-red-900/20 dark:border-red-800 satoshi-xs-medium-err-700 dark:text-red-400"
                    }`}
                >
                  {promotion.status}
                </span>
              </div>
              <Information
                icon={<DiscountIcon />}
                text="Discount Percentage"
                content={promotion.discount + " Discount"}
              />
              <Information
                icon={<UsageIcon />}
                text="Usage Count"
                content={promotion.usage}
              />
              <Information
                icon={<UserGroupIcon />}
                text="User Group"
                content={promotion.group}
              />
            </div>

            {/* Sharable Link */}
            <button
              onClick={() => handleCopyClick(promotion.link)}
              className="w-full rounded-lg border border-gray-300 py-3 px-4 flex items-center justify-center gap-2 dark:border-zinc-700 dark:hover:bg-zinc-800 transition-colors"
            >
              <span className="satoshi-base-bold-gray-900 text-nowrap dark:text-white text-sm">
                Copy sharable link
              </span>
              <ShareIcon />
            </button>
          </div>
        );
      })}
    </div>
  );
};

const Information = ({ icon, text, content }: InfoProp) => {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-1.5">
        {icon}
        <span className="satoshi-xs-normal-gray-500 dark:text-zinc-400">
          {text}
        </span>
      </div>
      <span className="satoshi-sm-medium-gray-900 dark:text-zinc-200">
        {content}
      </span>
    </div>
  );
};

export const PromotionModal = ({ close }: { close: () => void }) => {
  const Divider = () => (
    <div className="w-full border-[0.5px] border-gray-200 dark:border-zinc-700 my-1"></div>
  );

  return (
    // Outer Backdrop Intercept: Centers layout and sets 16px safety perimeter
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-xl">
        <ModalContainer
          title="Create new promotion"
          closeIcon={
            <button
              onClick={close}
              className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-[#FFFFFF1A]"
            >
              <CloseIcon />
            </button>
          }
        >
          {/* Scroll container wrapper gives clean accessibility to all 6 sub-sections */}
          <div className="mt-3 max-h-[calc(100vh-180px)] overflow-y-auto px-1 pr-1.5 flex flex-col gap-4 scrollbar-thin">
            {/* Promotion Details */}
            <HeadingContainer title="Promotion Details">
              <div className="flex flex-col gap-3">
                <Input placeholder="Promotion name" type="text" />
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full">
                  <div className="flex-1">
                    <Input placeholder="Valid till (DD/MM/YY)" type="date" />
                  </div>
                  <div className="hidden sm:block h-10 border border-gray-300 dark:border-zinc-700"></div>
                  <div className="flex-shrink-0 pt-1 sm:pt-0">
                    <Toggle text="No End" />
                  </div>
                </div>
              </div>
            </HeadingContainer>

            <Divider />

            {/* Countries Availability */}
            <HeadingContainer title="Countries Availability">
              <Select options={countries} type="select" />
            </HeadingContainer>

            <Divider />

            {/* Discount Details */}
            <HeadingContainer title="Discount Details">
              <div className="flex flex-col sm:flex-row gap-3.5 items-start sm:items-center">
                <RadioGroup
                  name="discount"
                  dir="row"
                  options={[
                    { label: "Percentage", value: "one" },
                    { label: "Fixed amount", value: "two" },
                  ]}
                  onChange={(val) => console.log("Selected:", val)}
                />
                <div className="w-full sm:w-auto flex-1">
                  <Input
                    placeholder="Amount"
                    icon={{ icon: <PercentIcon /> }}
                  />
                </div>
              </div>
              <Select options={noLimit} type="select" />
            </HeadingContainer>

            <Divider />

            {/* Minimum Purchase Requirements */}
            <HeadingContainer title="Minimum Purchase Requirements">
              <RadioGroup
                name="purchase requirements"
                dir="col"
                options={[
                  { label: "No minimum requirement", value: "one" },
                  { label: "Minimum purchase amount", value: "two" },
                  { label: "Minimum quality of items", value: "three" },
                ]}
                onChange={(val) => console.log("Selected:", val)}
              />
            </HeadingContainer>

            <Divider />

            {/* User Group */}
            <HeadingContainer title="User Group">
              <RadioGroup
                name="user group"
                dir="col"
                options={[
                  { label: "All users", value: "one" },
                  { label: "Specific user groups", value: "two" },
                ]}
                onChange={(val) => console.log("Selected:", val)}
              />
            </HeadingContainer>

            <Divider />

            {/* Promotion Code */}
            <HeadingContainer title="Promotion Code">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-start gap-3">
                <div className="flex-1 flex flex-col gap-1">
                  <Input
                    placeholder="Discount code"
                    icon={{
                      onClick: () => handleCopyClick(""),
                      icon: <CopyIcon />,
                    }}
                  />
                  <span className="satoshi-sm-normal-gray-500 dark:text-white/70 text-xs pl-1">
                    Generate code or type in code
                  </span>
                </div>
                <button
                  onClick={() => {}}
                  className="flex items-center justify-center gap-2 border border-gray-300 dark:border-zinc-700 rounded-lg h-12 px-5 dark:text-white hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors"
                >
                  <GenerateIcon />
                  <span className="satoshi-base-bold-gray-900 dark:text-white text-sm">
                    Generate
                  </span>
                </button>
              </div>
            </HeadingContainer>

            {/* Modal Action Footer buttons */}
            <div className="flex items-center justify-end gap-3 pt-2 pb-1 mt-auto">
              <OutlineButton content="Cancel" />
              <GradientButton content="Create Promotion" />
            </div>
          </div>
        </ModalContainer>
      </div>
    </div>
  );
};

const HeadingContainer = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => {
  return (
    <div className="flex flex-col gap-2.5 w-full">
      <span className="satoshi-base-bold-pry-900 dark:text-white text-sm font-semibold">
        {title}
      </span>
      {children}
    </div>
  );
};

const countries = ["Nigeria", "Ghana", "Egypt"];
const noLimit = ["Yes", "No"];
